import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import AuthorAvatars from "components/AuthorAvatars"
import Button from "components/Button"
import Modal from "components/Modal"
import { TUTORIAL_GATING_FORM_ID } from "constants/hubspotFormIds"
import { trackEvent } from "lib/googleTagManager"
import { H3, H4, P2, P3 } from "components/Typography"
import * as css from "./TutorialModal.module.css"

const HUBSPOT_PORTAL_ID = "5901754"

/**
 * Simple email gating form that submits directly to HubSpot API
 */
function EmailGateForm({ onSuccess }) {
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setIsSubmitting(true)

    const fields = [{ name: "email", value: email }]
    if (firstName) fields.push({ name: "firstname", value: firstName })
    if (lastName) fields.push({ name: "lastname", value: lastName })

    try {
      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${TUTORIAL_GATING_FORM_ID}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fields,
            context: {
              pageUri:
                typeof window !== "undefined" ? window.location.href : "",
              pageName: "Tutorials",
            },
          }),
        }
      )

      if (response.ok) {
        onSuccess?.()
      } else {
        const data = await response.json().catch(() => ({}))
        setError(data.message || "Submission failed. Please try again.")
      }
    } catch {
      setError("Network error. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className={css.emailForm}>
      <div className={css.nameRow}>
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          className={css.emailInput}
          disabled={isSubmitting}
        />
        <input
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name"
          className={css.emailInput}
          disabled={isSubmitting}
        />
      </div>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Work email *"
        required
        className={css.emailInput}
        disabled={isSubmitting}
      />
      {error && <p className={css.errorMessage}>{error}</p>}
      <Button
        type="submit"
        color="green"
        large
        style={{ width: "100%" }}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Unlocking..." : "Unlock Videos"}
      </Button>
    </form>
  )
}

const DEFAULT_THUMBNAIL = "/images/tutorials/default-thumbnail.jpg"

function getShareUrl(slug) {
  if (typeof window === "undefined") return ""
  const url = new URL(window.location.href)
  url.searchParams.set("watch", slug)
  return url.toString()
}

function CopyLinkButton({ slug }) {
  const [copied, setCopied] = useState(false)
  const resetTimeoutRef = useRef(null)

  useEffect(() => {
    return () => clearTimeout(resetTimeoutRef.current)
  }, [])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl(slug))
      setCopied(true)
      clearTimeout(resetTimeoutRef.current)
      resetTimeoutRef.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — nothing to do
    }
  }

  return (
    <button type="button" className={css.copyButton} onClick={handleCopy}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9403 15.7513 14.6897C16.4231 14.4392 17.0331 14.047 17.54 13.54L20.54 10.54C21.4508 9.59695 21.9548 8.33394 21.9434 7.02296C21.932 5.71198 21.4061 4.45791 20.4791 3.53087C19.5521 2.60383 18.298 2.07799 16.987 2.06658C15.676 2.05517 14.413 2.55915 13.47 3.47L11.75 5.18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 11C13.5705 10.4259 13.0226 9.95083 12.3934 9.60706C11.7642 9.26329 11.0685 9.05886 10.3533 9.00765C9.63812 8.95643 8.92037 9.05965 8.24864 9.31023C7.5769 9.5608 6.96687 9.95296 6.46 10.46L3.46 13.46C2.54916 14.403 2.04518 15.6661 2.05659 16.977C2.068 18.288 2.59385 19.5421 3.52089 20.4691C4.44793 21.3961 5.70201 21.922 7.01299 21.9334C8.32397 21.9448 9.58697 21.4408 10.53 20.53L12.24 18.82"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span aria-live="polite">{copied ? "Copied!" : "Copy link"}</span>
    </button>
  )
}

function RelatedTutorialCard({ tutorial, onClick }) {
  return (
    <button
      type="button"
      className={css.relatedCard}
      onClick={() => onClick(tutorial)}
    >
      <div className={css.relatedThumbnail}>
        <img
          src={tutorial.thumbnail?.url || DEFAULT_THUMBNAIL}
          alt={tutorial.thumbnail?.alt || tutorial.title}
        />
        {tutorial.duration && (
          <span className={css.relatedDuration}>{tutorial.duration} min</span>
        )}
      </div>
      <div className={css.relatedInfo}>
        <P3 className={css.relatedTitle}>{tutorial.title}</P3>
        {tutorial.category && (
          <span className={css.relatedCategory}>{tutorial.category}</span>
        )}
      </div>
    </button>
  )
}

function TutorialModal({
  tutorial,
  allTutorials = [],
  hasAccess,
  isLoading = false,
  onGrantAccess,
  onClose,
  onSelectTutorial,
}) {
  const [carouselIndex, setCarouselIndex] = useState(0)

  const handleFormSubmit = () => {
    trackEvent("tutorialGatingFormSubmission", {
      tutorialTitle: tutorial?.title,
      tutorialSlug: tutorial?.slug,
    })
    onGrantAccess?.()
  }

  if (!tutorial) return null

  // Show loading state while checking HubSpot customer status
  if (isLoading) {
    return (
      <Modal
        onClose={onClose}
        size="large"
        className={css.modal}
        title={tutorial.title}
      >
        <div className={css.loadingWrapper}>
          <P2>Checking access...</P2>
        </div>
      </Modal>
    )
  }

  // Get related tutorials from explicit relatedVideos slugs
  const relatedTutorials = (tutorial.relatedVideos || [])
    .map((slug) => allTutorials.find((t) => t.slug === slug))
    .filter(Boolean)

  const showCarousel = relatedTutorials.length > 3
  const visibleCount = 3
  const maxIndex = Math.max(0, relatedTutorials.length - visibleCount)

  const handlePrev = () => {
    setCarouselIndex((prev) => Math.max(0, prev - 1))
  }

  const handleNext = () => {
    setCarouselIndex((prev) => Math.min(maxIndex, prev + 1))
  }

  return (
    <Modal
      onClose={onClose}
      size="large"
      className={css.modal}
      title={tutorial.title}
    >
      {hasAccess ? (
        <div className={css.contentWrapper}>
          <div className={css.mainContent}>
            <div className={css.videoWrapper}>
              {tutorial.youtubeId ? (
                <iframe
                  className={css.video}
                  src={`https://www.youtube-nocookie.com/embed/${tutorial.youtubeId}?autoplay=1&rel=0`}
                  title={tutorial.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; compute-pressure"
                  // Without an explicit referrer, youtube-nocookie can't verify
                  // the embedding origin and the player throws "Video player
                  // configuration error" (Error 153). Matches YoutubeVideo.
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <video
                  className={css.video}
                  src={tutorial.videoUrl}
                  poster={tutorial.thumbnail?.url || DEFAULT_THUMBNAIL}
                  controls
                  autoPlay
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
            <div className={css.infoSection}>
              <div className={css.meta}>
                {tutorial.category && (
                  <span className={css.category}>{tutorial.category}</span>
                )}
                {tutorial.date && (
                  <span className={css.date}>{tutorial.date}</span>
                )}
                {tutorial.duration && (
                  <span className={css.duration}>{tutorial.duration} min</span>
                )}
                {tutorial.slug && <CopyLinkButton slug={tutorial.slug} />}
              </div>
              {tutorial.author && (
                <div className={css.authors}>
                  <AuthorAvatars author={tutorial.author} size={28} />
                </div>
              )}
              {tutorial.description && (
                <P2 className={css.description}>{tutorial.description}</P2>
              )}
            </div>
          </div>

          {relatedTutorials.length > 0 && (
            <div className={css.relatedSection}>
              <div className={css.relatedHeader}>
                <H4 className={css.relatedHeading}>More Tutorials</H4>
                {showCarousel && (
                  <div className={css.carouselNav}>
                    <button
                      type="button"
                      className={css.carouselBtn}
                      onClick={handlePrev}
                      disabled={carouselIndex === 0}
                      aria-label="Previous"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className={css.carouselBtn}
                      onClick={handleNext}
                      disabled={carouselIndex >= maxIndex}
                      aria-label="Next"
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
              <div className={css.carouselWrapper}>
                <div
                  className={css.relatedGrid}
                  style={{
                    transform: showCarousel
                      ? `translateX(calc(-${carouselIndex} * (var(--card-width) + var(--card-gap))))`
                      : undefined,
                  }}
                >
                  {relatedTutorials.map((related) => (
                    <RelatedTutorialCard
                      key={related.id}
                      tutorial={related}
                      onClick={onSelectTutorial}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={css.gatingWrapper}>
          <div className={css.gatingContent}>
            <H3 className={css.gatingTitle}>Unlock Free Tutorial Videos</H3>
            <P2 className={css.gatingDescription}>
              Fill out the form below to get instant access to all tutorial
              videos. You only need to do this once.
            </P2>
            <div className={css.formWrapper}>
              <EmailGateForm onSuccess={handleFormSubmit} />
            </div>
          </div>
          <div className={css.previewWrapper}>
            <div className={css.previewThumbnail}>
              <img
                src={tutorial.thumbnail?.url || DEFAULT_THUMBNAIL}
                alt={tutorial.thumbnail?.alt || tutorial.title}
                className={css.previewImage}
              />
              <div className={css.previewOverlay}>
                <span className={css.lockIcon}>
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
            <P2 className={css.previewTitle}>{tutorial.title}</P2>
          </div>
        </div>
      )}
    </Modal>
  )
}

CopyLinkButton.propTypes = {
  slug: PropTypes.string.isRequired,
}

RelatedTutorialCard.propTypes = {
  tutorial: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    category: PropTypes.string,
    duration: PropTypes.number,
    videoUrl: PropTypes.string,
    thumbnail: PropTypes.shape({
      url: PropTypes.string,
      alt: PropTypes.string,
    }),
  }),
  onClick: PropTypes.func,
}

TutorialModal.propTypes = {
  tutorial: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    author: PropTypes.string,
    category: PropTypes.string,
    description: PropTypes.string,
    date: PropTypes.string,
    duration: PropTypes.number,
    videoUrl: PropTypes.string,
    youtubeId: PropTypes.string,
    thumbnail: PropTypes.shape({
      url: PropTypes.string,
      alt: PropTypes.string,
    }),
    relatedVideos: PropTypes.arrayOf(PropTypes.string),
  }),
  allTutorials: PropTypes.arrayOf(PropTypes.object),
  hasAccess: PropTypes.bool,
  isLoading: PropTypes.bool,
  onGrantAccess: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSelectTutorial: PropTypes.func,
}

export default TutorialModal
